// Admin OCF review
const OCF_VALUE_DOMAIN_ORDER = ['country', 'state', 'currency', 'status', 'source'];

const AdminOcfScreen = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [workflow, setWorkflow] = useState('columns');
  const [domainKey, setDomainKey] = useState('country');
  const [query, setQuery] = useState('');
  const [drafts, setDrafts] = useState({});
  const [savingRule, setSavingRule] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState('');

  const loadOcf = useCallback(async () => {
    setLoading(true);
    setError('');
    const { ok, data: response } = await api('GET', '/api/admin/ocf');
    setLoading(false);
    if (!ok) {
      setError(response?.error || 'Could not load OCF rules.');
      return;
    }
    setData(response);
  }, []);

  useEffect(() => { loadOcf(); }, [loadOcf]);

  const loadAiSuggestions = useCallback(async () => {
    setSuggestionsLoading(true);
    setSuggestionsError('');
    const { ok, data: response } = await api('POST', '/api/admin/ocf/suggestions');
    setSuggestionsLoading(false);
    if (response?.suggestions && typeof response.suggestions === 'object') {
      setAiSuggestions(response.suggestions);
    }
    if (!ok) {
      setSuggestionsError(response?.error || 'AI suggestions could not load.');
    } else if (response?.warning) {
      setSuggestionsError(response.warning);
    }
  }, []);

  useEffect(() => {
    if (!data) return;
    const needsReview = Number(data?.summary?.needsReview || 0);
    if (!needsReview) {
      setAiSuggestions({});
      setSuggestionsLoading(false);
      setSuggestionsError('');
      return;
    }
    setAiSuggestions({});
    loadAiSuggestions();
  }, [data?.generatedAt, data?.summary?.needsReview, loadAiSuggestions]);

  const domains = data?.catalog?.domains || [];
  const selectedDomain = domains.find(domain => domain.key === domainKey) || domains[0] || null;
  const columnFields = data?.catalog?.column?.fields || [];
  const selectedColumnField = key => columnFields.find(field => field.key === key);
  const suggestionForRow = row => row?.reviewKey ? aiSuggestions[row.reviewKey] : null;

  const rowKey = row => `${workflow}:${workflow === 'columns' ? 'column' : domainKey}:${row.raw || ''}:${row.canonical || ''}:${row.country || ''}`;
  const defaultDraftForRow = row => {
    const suggestion = suggestionForRow(row);
    if (!row.known && suggestion?.canonical) {
      if (workflow === 'columns') {
        return {
          canonical: suggestion.canonical,
          label: selectedColumnField(suggestion.canonical)?.label || suggestion.label || '',
        };
      }
      return {
        canonical: suggestion.canonical,
        label: suggestion.label || '',
      };
    }
    if (workflow === 'columns') {
      const canonical = row.canonical || '';
      return { canonical, label:selectedColumnField(canonical)?.label || row.label || '' };
    }
    return {
      canonical: row.canonical || row.raw || '',
      label: row.label && !String(row.label).startsWith('Uncataloged') && row.label !== 'Source region' ? row.label : '',
    };
  };
  const draftForRow = row => drafts[rowKey(row)] || defaultDraftForRow(row);
  const updateDraft = (row, patch) => {
    const key = rowKey(row);
    setDrafts(prev => ({ ...prev, [key]: { ...defaultDraftForRow(row), ...(prev[key] || {}), ...patch } }));
  };
  const applySuggestion = (row, suggestion) => {
    if (!suggestion?.canonical) return;
    updateDraft(row, {
      canonical: suggestion.canonical,
      label: workflow === 'columns'
        ? (selectedColumnField(suggestion.canonical)?.label || suggestion.label || '')
        : (suggestion.label || ''),
    });
  };

  const approveObservedRow = async row => {
    const key = rowKey(row);
    const draft = draftForRow(row);
    const raw = row.raw || row.canonical;
    const domain = workflow === 'columns' ? 'column' : domainKey;
    if (!raw) return;
    if (!String(draft.canonical || '').trim()) {
      setError(workflow === 'columns' ? 'Choose an Omnifill field.' : 'OCF value is required.');
      return;
    }
    setSavingRule(key);
    setError('');
    const { ok, data: response } = await api('POST', '/api/admin/ocf/rules', {
      domain,
      raw,
      canonical: draft.canonical,
      label: draft.label,
      country: workflow === 'values' ? (row.country || '') : '',
    });
    if (!ok) {
      setError(response?.error || 'Could not approve OCF rule.');
      setSavingRule('');
      return;
    }
    setDrafts(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    await loadOcf();
    setSavingRule('');
  };

  const removeCatalogRule = async rule => {
    if (!rule?.id || rule.origin !== 'admin') return;
    const key = `rule:${rule.id}`;
    setSavingRule(key);
    setError('');
    const { ok, data: response } = await api('DELETE', `/api/admin/ocf/rules/${rule.id}`);
    if (!ok) {
      setError(response?.error || 'Could not remove OCF rule.');
      setSavingRule('');
      return;
    }
    await loadOcf();
    setSavingRule('');
  };

  const normalizedQuery = query.trim().toLowerCase();
  const matchesQuery = (row, fields) => !normalizedQuery || fields
    .some(field => String(row[field] || '').toLowerCase().includes(normalizedQuery));

  const observedRank = row => {
    if (!row.raw && !row.canonical) return 4;
    if (!row.known) return 0;
    if (row.changed) return 1;
    return 2;
  };
  const sortObservedValues = rows => [...rows].sort((a, b) => {
    const rankDelta = observedRank(a) - observedRank(b);
    if (rankDelta) return rankDelta;
    const countDelta = Number(b.count || 0) - Number(a.count || 0);
    if (countDelta) return countDelta;
    return String(a.raw || a.canonical || '').localeCompare(String(b.raw || b.canonical || ''));
  });

  const observedValueRows = sortObservedValues(data?.observed?.[domainKey] || []);
  const valueCatalogRows = selectedDomain?.rules || [];
  const observedColumnRows = data?.observed?.columns || [];
  const columnCatalogRows = data?.catalog?.column?.rules || [];

  const filteredObservedValues = observedValueRows.filter(row => matchesQuery(row, ['raw', 'canonical', 'label', 'country']));
  const filteredValueCatalog = valueCatalogRows.filter(row => matchesQuery(row, ['raw', 'canonical', 'label', 'kind', 'origin', 'country']));
  const filteredObservedColumns = observedColumnRows.filter(row => matchesQuery({ ...row, sources:(row.sources || []).join(', ') }, ['raw', 'canonical', 'label', 'sources']));
  const filteredColumnCatalog = columnCatalogRows.filter(row => matchesQuery(row, ['raw', 'canonical', 'label', 'kind', 'origin']));

  const statusPill = row => {
    if (!row.raw && !row.canonical) {
      return <span style={{ display:'inline-flex', borderRadius:5, padding:'3px 7px', background:'#F3F4F6', color:'#6B7280', fontSize:11, fontWeight:900 }}>Empty</span>;
    }
    if (!row.known) {
      return <span style={{ display:'inline-flex', alignItems:'center', gap:5, borderRadius:5, padding:'3px 7px', background:'#FEF3C7', color:'#92400E', fontSize:11, fontWeight:900 }}><AlertTriangle size={12}/> Review</span>;
    }
    if (workflow === 'values' && row.changed) {
      return <span style={{ display:'inline-flex', alignItems:'center', gap:5, borderRadius:5, padding:'3px 7px', background:'#DBEAFE', color:'#1D4ED8', fontSize:11, fontWeight:900 }}><CheckCircle2 size={12}/> Normalized</span>;
    }
    return <span style={{ display:'inline-flex', alignItems:'center', gap:5, borderRadius:5, padding:'3px 7px', background:'#DCFCE7', color:'#166534', fontSize:11, fontWeight:900 }}><Check size={12}/> Mapped</span>;
  };

  const summaryCards = [
    ['VALUE RULES', data?.summary?.valueCatalogRules || 0],
    ['COLUMN RULES', data?.summary?.columnCatalogRules || 0],
    ['VALUE OBSERVED', data?.summary?.observedValues || 0],
    ['COLUMN HEADERS', data?.summary?.observedColumns || 0],
    ['NEEDS REVIEW', data?.summary?.needsReview ?? ((data?.summary?.uncatalogedValues || 0) + (data?.summary?.uncatalogedColumns || 0))],
  ];

  const workflowButton = (id, label, sub, Icon) => {
    const active = workflow === id;
    return (
      <button type="button" onClick={() => { setWorkflow(id); setQuery(''); }} style={{ textAlign:'left', border:`1px solid ${active ? '#BFD9FF' : '#E5E7EB'}`, background:active ? '#EEF6FF' : '#fff', color:'#111827', borderRadius:8, padding:12, display:'flex', gap:10, alignItems:'flex-start', cursor:'pointer' }}>
        <Icon size={17} color={active ? '#006FDD' : '#6B7280'} style={{ flexShrink:0, marginTop:1 }}/>
        <span>
          <span style={{ display:'block', fontSize:13, fontWeight:900 }}>{label}</span>
          <span style={{ display:'block', fontSize:11, color:'#6B7280', marginTop:2, lineHeight:1.35 }}>{sub}</span>
        </span>
      </button>
    );
  };

  const searchBox = (
    <div style={{ position:'relative' }}>
      <Search size={14} style={{ position:'absolute', left:10, top:9, color:'#9CA3AF' }}/>
      <input value={query} onChange={e => setQuery(e.target.value)} placeholder={workflow === 'columns' ? 'Search column aliases' : 'Search value aliases'} style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'8px 10px 8px 30px', width:250, fontSize:12, outline:'none' }}/>
    </div>
  );

  const renderSuggestion = row => {
    if (row.known || !(row.raw || row.canonical)) return <span style={{ color:'#9CA3AF' }}>-</span>;
    const suggestion = suggestionForRow(row);
    if (suggestionsLoading && !suggestion) return <SkeletonPill width={140} height={14}/>;
    if (!suggestion) return <span style={{ color:'#9CA3AF' }}>-</span>;
    if (!suggestion.canonical) {
      return (
        <div style={{ color:'#6B7280', lineHeight:1.35 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, fontWeight:900, color:'#92400E' }}><Sparkles size={13}/>No confident suggestion</div>
          {suggestion.reason && <div style={{ marginTop:2, fontSize:11 }}>{suggestion.reason}</div>}
        </div>
      );
    }
    return (
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:10 }}>
        <div style={{ minWidth:0, lineHeight:1.35 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6, color:'#0058B8', fontWeight:900 }}>
            <Sparkles size={13}/>
            <span style={{ fontFamily:'monospace' }}>{suggestion.canonical}</span>
            <span style={{ color:'#6B7280', fontSize:10, fontWeight:900 }}>{suggestion.confidence}</span>
          </div>
          {suggestion.label && <div style={{ marginTop:2, color:'#4B5563', overflowWrap:'anywhere' }}>{suggestion.label}</div>}
          {suggestion.reason && <div style={{ marginTop:2, color:'#9CA3AF', fontSize:11, overflowWrap:'anywhere' }}>{suggestion.reason}</div>}
        </div>
        <button type="button" onClick={() => applySuggestion(row, suggestion)} style={{ border:'1px solid #BFD9FF', background:'#fff', color:'#0058B8', borderRadius:6, padding:'5px 8px', fontSize:11, fontWeight:900, cursor:'pointer', flexShrink:0 }}>
          Use
        </button>
      </div>
    );
  };

  return (
    <div className="dash-page-scroll workspace-page">
      <div className="dash-panel workspace-panel-flat" style={{ marginBottom:14 }}>
        <div className="dash-panel-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16 }}>
          <div style={{ minWidth:0, flex:1 }}>
            <h1 className="dash-page-title">OCF Review</h1>
            <p className="dash-page-subtitle" style={{ maxWidth:800 }}>
              Manage CSV column aliases and raw value aliases used by Smart CSV import and order normalization.
            </p>
          </div>
          <button onClick={loadOcf} disabled={loading} style={{ marginLeft:'auto', flexShrink:0, background:loading ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'9px 12px', fontSize:12, fontWeight:900, cursor:loading ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:7 }}>
            <RefreshCw size={14}/>{loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:10, marginBottom:14 }}>
        {summaryCards.map(([label, value]) => (
          <div key={label} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:13 }}>
            <div style={{ fontSize:11, color:'#6B7280', fontWeight:900 }}>{label}</div>
            <div style={{ fontSize:24, fontWeight:900, color:'#1A1A2E', marginTop:5 }}>{loading ? <SkeletonPill width={42} height={24}/> : value}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))', gap:10, marginBottom:14 }}>
        {workflowButton('columns', 'Column aliases', 'Teach Smart CSV which uploaded headers map to Omnifill fields.', FileSpreadsheet)}
        {workflowButton('values', 'Value aliases', 'Teach OCF how raw countries, states, currencies, statuses, and sources normalize.', SlidersHorizontal)}
      </div>

      <div className="dash-panel workspace-panel-flat" style={{ overflow:'hidden', marginBottom:14 }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #F3F4F6', display:'flex', gap:10, alignItems:'center', justifyContent:'space-between', flexWrap:'wrap' }}>
          {workflow === 'values' ? (
            <div style={{ display:'flex', gap:7, alignItems:'center', flexWrap:'wrap' }}>
              {OCF_VALUE_DOMAIN_ORDER.map(key => {
                const domain = domains.find(item => item.key === key);
                if (!domain) return null;
                const active = domainKey === key;
                return (
                  <button key={key} type="button" onClick={() => setDomainKey(key)} style={{ border:`1px solid ${active ? '#006FDD' : '#E5E7EB'}`, background:active ? '#EEF6FF' : '#fff', color:active ? '#0058B8' : '#374151', borderRadius:7, padding:'7px 10px', fontSize:12, fontWeight:900, cursor:'pointer' }}>
                    {domain.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize:13, fontWeight:900, color:'#111827' }}>Smart CSV column mapping</div>
          )}
          {searchBox}
        </div>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #F3F4F6', display:'flex', gap:9, alignItems:'flex-start', background:'#F9FAFB' }}>
          {workflow === 'columns' ? <FileSpreadsheet size={15} color="#006FDD" style={{ flexShrink:0, marginTop:2 }}/> : <SlidersHorizontal size={15} color="#006FDD" style={{ flexShrink:0, marginTop:2 }}/>}
          <div style={{ fontSize:12, color:'#4B5563', lineHeight:1.45 }}>
            {workflow === 'columns' ? data?.catalog?.column?.description : selectedDomain?.description}
          </div>
        </div>
        {error && <div style={{ padding:'10px 14px', borderTop:'1px solid #FEE2E2', background:'#FEF2F2', color:'#991B1B', fontSize:12, fontWeight:800 }}>{error}</div>}
        {(suggestionsLoading || suggestionsError) && (
          <div style={{ padding:'10px 14px', borderTop:'1px solid #F3F4F6', background:suggestionsError ? '#FFFBEB' : '#F9FAFB', color:suggestionsError ? '#92400E' : '#4B5563', fontSize:12, fontWeight:800, display:'flex', alignItems:'center', gap:7 }}>
            <Sparkles size={14}/>{suggestionsError || 'Preparing AI suggestions for rows that need review...'}
          </div>
        )}
      </div>

      {workflow === 'columns' ? (
        <>
          <div className="dash-panel workspace-panel-flat" style={{ overflow:'hidden', marginBottom:14 }}>
            <div style={{ padding:'11px 14px', fontSize:12, fontWeight:900, color:'#111827', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <span>Observed CSV headers</span>
              {!loading && <span style={{ color:'#6B7280', fontSize:11, fontWeight:800 }}>{filteredObservedColumns.length} shown</span>}
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', minWidth:1080, borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                    {['Raw header', 'Omnifill field', 'Sources', 'Rows', 'Status', 'AI suggestion', 'Action'].map(header => (
                      <th key={header} style={{ textAlign:'left', padding:'9px 11px', color:'#6B7280', fontSize:11, fontWeight:900 }}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} style={{ padding:16, color:'#9CA3AF' }}>Loading observed headers...</td></tr>
                  ) : filteredObservedColumns.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding:16, color:'#9CA3AF' }}>No observed headers match this search.</td></tr>
                  ) : filteredObservedColumns.map((row, index) => {
                    const key = rowKey(row);
                    const draft = draftForRow(row);
                    const saving = savingRule === key;
                    const field = selectedColumnField(draft.canonical);
                    return (
                      <tr key={`${row.raw}-${row.canonical}-${index}`} style={{ borderBottom:'1px solid #F3F4F6', background:!row.known ? '#FFFBEB' : '#fff' }}>
                        <td style={{ padding:'9px 11px', color:'#374151', maxWidth:220, overflowWrap:'anywhere' }}>{row.raw || '-'}</td>
                        <td style={{ padding:'9px 11px', color:'#111827', fontWeight:900 }}>{row.canonical ? `${row.canonical} (${row.label || field?.label || 'Field'})` : '-'}</td>
                        <td style={{ padding:'9px 11px', color:'#6B7280' }}>{(row.sources || []).join(', ') || '-'}</td>
                        <td style={{ padding:'9px 11px', color:'#374151', fontWeight:900 }}>{row.count}</td>
                        <td style={{ padding:'9px 11px' }}>{statusPill(row)}</td>
                        <td style={{ padding:'7px 11px', minWidth:260 }}>{renderSuggestion(row)}</td>
                        <td style={{ padding:'7px 11px', minWidth:280 }}>
                          {!row.known ? (
                            <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
                              <select value={draft.canonical} onChange={e => updateDraft(row, { canonical:e.target.value, label:selectedColumnField(e.target.value)?.label || '' })} disabled={saving} style={{ width:170, border:'1px solid #E5E7EB', borderRadius:6, padding:'6px 7px', fontSize:12, outline:'none', background:saving ? '#F9FAFB' : '#fff' }}>
                                <option value="">Choose field</option>
                                {columnFields.map(field => <option key={field.key} value={field.key}>{field.label}</option>)}
                              </select>
                              <button type="button" onClick={() => approveObservedRow(row)} disabled={saving || !draft.canonical} style={{ border:'1px solid #BFD9FF', background:saving ? '#F3F4F6' : '#EEF6FF', color:saving ? '#6B7280' : '#0058B8', borderRadius:6, padding:'6px 9px', fontSize:11, fontWeight:900, cursor:saving ? 'wait' : 'pointer' }}>
                                {saving ? 'Saving...' : 'Approve'}
                              </button>
                            </div>
                          ) : (
                            <span style={{ color:'#9CA3AF' }}>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="dash-panel workspace-panel-flat" style={{ overflow:'hidden', marginBottom:14 }}>
            <div style={{ padding:'11px 14px', fontSize:12, fontWeight:900, color:'#111827', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <span>Column alias catalog</span>
              {!loading && <span style={{ color:'#6B7280', fontSize:11, fontWeight:800 }}>{filteredColumnCatalog.length} shown</span>}
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', minWidth:700, borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                    {['Raw header', 'Omnifill field', 'Label', 'Source', 'Action'].map(header => (
                      <th key={header} style={{ textAlign:'left', padding:'9px 11px', color:'#6B7280', fontSize:11, fontWeight:900 }}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ padding:16, color:'#9CA3AF' }}>Loading column rules...</td></tr>
                  ) : filteredColumnCatalog.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding:16, color:'#9CA3AF' }}>No column rules match this search.</td></tr>
                  ) : filteredColumnCatalog.map((rule, index) => (
                    <tr key={`${rule.raw}-${rule.canonical}-${index}`} style={{ borderBottom:'1px solid #F3F4F6' }}>
                      <td style={{ padding:'9px 11px', color:'#374151', maxWidth:220, overflowWrap:'anywhere' }}>{rule.raw}</td>
                      <td style={{ padding:'9px 11px', color:'#111827', fontFamily:'monospace', fontWeight:900 }}>{rule.canonical}</td>
                      <td style={{ padding:'9px 11px', color:'#6B7280' }}>{rule.label}</td>
                      <td style={{ padding:'9px 11px', color:rule.origin === 'admin' ? '#0058B8' : '#6B7280', fontWeight:900 }}>{rule.origin === 'admin' ? 'Admin' : 'Base'}</td>
                      <td style={{ padding:'7px 11px' }}>
                        {rule.origin === 'admin' ? (
                          <button type="button" onClick={() => removeCatalogRule(rule)} disabled={savingRule === `rule:${rule.id}`} style={{ border:'1px solid #FECACA', background:savingRule === `rule:${rule.id}` ? '#F3F4F6' : '#FEF2F2', color:savingRule === `rule:${rule.id}` ? '#6B7280' : '#B91C1C', borderRadius:6, padding:'6px 9px', fontSize:11, fontWeight:900, cursor:savingRule === `rule:${rule.id}` ? 'wait' : 'pointer' }}>
                            {savingRule === `rule:${rule.id}` ? 'Removing...' : 'Remove'}
                          </button>
                        ) : <span style={{ color:'#9CA3AF' }}>-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="dash-panel workspace-panel-flat" style={{ overflow:'hidden', marginBottom:14 }}>
            <div style={{ padding:'11px 14px', fontSize:12, fontWeight:900, color:'#111827', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <span>Observed raw values</span>
              {!loading && <span style={{ color:'#6B7280', fontSize:11, fontWeight:800 }}>{filteredObservedValues.length} shown</span>}
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', minWidth:1060, borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                    {['Raw value', 'OCF value', domainKey === 'state' ? 'Country' : 'Label', 'Orders', 'Status', 'AI suggestion', 'Action'].map(header => (
                      <th key={header} style={{ textAlign:'left', padding:'9px 11px', color:'#6B7280', fontSize:11, fontWeight:900 }}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={7} style={{ padding:16, color:'#9CA3AF' }}>Loading observed values...</td></tr>
                  ) : filteredObservedValues.length === 0 ? (
                    <tr><td colSpan={7} style={{ padding:16, color:'#9CA3AF' }}>No observed values match this search.</td></tr>
                  ) : filteredObservedValues.map((row, index) => {
                    const key = rowKey(row);
                    const draft = draftForRow(row);
                    const canApprove = !row.known && (row.raw || row.canonical);
                    const saving = savingRule === key;
                    return (
                      <tr key={`${row.raw}-${row.canonical}-${row.country}-${index}`} style={{ borderBottom:'1px solid #F3F4F6', background:canApprove ? '#FFFBEB' : '#fff' }}>
                        <td style={{ padding:'9px 11px', color:'#374151', maxWidth:190, overflowWrap:'anywhere' }}>{row.raw || '-'}</td>
                        <td style={{ padding:'9px 11px', color:'#111827', fontFamily:'monospace', fontWeight:900 }}>{row.canonical || '-'}</td>
                        <td style={{ padding:'9px 11px', color:'#6B7280' }}>{domainKey === 'state' ? (row.country || '-') : (row.label || '-')}</td>
                        <td style={{ padding:'9px 11px', color:'#374151', fontWeight:900 }}>{row.count}</td>
                        <td style={{ padding:'9px 11px' }}>{statusPill(row)}</td>
                        <td style={{ padding:'7px 11px', minWidth:260 }}>{renderSuggestion(row)}</td>
                        <td style={{ padding:'7px 11px', minWidth:250 }}>
                          {canApprove ? (
                            <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
                              <input value={draft.canonical} onChange={e => updateDraft(row, { canonical:e.target.value })} disabled={saving} placeholder="OCF" style={{ width:86, border:'1px solid #E5E7EB', borderRadius:6, padding:'6px 7px', fontSize:12, fontWeight:800, fontFamily:'monospace', outline:'none', background:saving ? '#F9FAFB' : '#fff' }}/>
                              <input value={draft.label} onChange={e => updateDraft(row, { label:e.target.value })} disabled={saving} placeholder="Label" style={{ width:132, border:'1px solid #E5E7EB', borderRadius:6, padding:'6px 7px', fontSize:12, outline:'none', background:saving ? '#F9FAFB' : '#fff' }}/>
                              <button type="button" onClick={() => approveObservedRow(row)} disabled={saving} style={{ border:'1px solid #BFD9FF', background:saving ? '#F3F4F6' : '#EEF6FF', color:saving ? '#6B7280' : '#0058B8', borderRadius:6, padding:'6px 9px', fontSize:11, fontWeight:900, cursor:saving ? 'wait' : 'pointer' }}>
                                {saving ? 'Saving...' : 'Approve'}
                              </button>
                            </div>
                          ) : <span style={{ color:'#9CA3AF' }}>-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="dash-panel workspace-panel-flat" style={{ overflow:'hidden', marginBottom:14 }}>
            <div style={{ padding:'11px 14px', fontSize:12, fontWeight:900, color:'#111827', borderBottom:'1px solid #F3F4F6', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
              <span>Value alias catalog</span>
              {!loading && <span style={{ color:'#6B7280', fontSize:11, fontWeight:800 }}>{filteredValueCatalog.length} shown</span>}
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', minWidth:640, borderCollapse:'collapse', fontSize:12 }}>
                <thead>
                  <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                    {['Raw', 'OCF', domainKey === 'state' ? 'Label / Country' : 'Label', 'Source', 'Action'].map(header => (
                      <th key={header} style={{ textAlign:'left', padding:'9px 11px', color:'#6B7280', fontSize:11, fontWeight:900 }}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} style={{ padding:16, color:'#9CA3AF' }}>Loading rules...</td></tr>
                  ) : filteredValueCatalog.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding:16, color:'#9CA3AF' }}>No rules match this search.</td></tr>
                  ) : filteredValueCatalog.map((rule, index) => (
                    <tr key={`${rule.raw}-${rule.canonical}-${index}`} style={{ borderBottom:'1px solid #F3F4F6' }}>
                      <td style={{ padding:'9px 11px', color:'#374151', maxWidth:170, overflowWrap:'anywhere' }}>{rule.raw}</td>
                      <td style={{ padding:'9px 11px', color:'#111827', fontFamily:'monospace', fontWeight:900 }}>{rule.canonical}</td>
                      <td style={{ padding:'9px 11px', color:'#6B7280' }}>
                        <div>{rule.label}</div>
                        {domainKey === 'state' && rule.country && <div style={{ marginTop:2, fontSize:11, color:'#9CA3AF', fontWeight:800 }}>Country: {rule.country}</div>}
                      </td>
                      <td style={{ padding:'9px 11px', color:rule.origin === 'admin' ? '#0058B8' : '#6B7280', fontWeight:900 }}>{rule.origin === 'admin' ? 'Admin' : 'Base'}</td>
                      <td style={{ padding:'7px 11px' }}>
                        {rule.origin === 'admin' ? (
                          <button type="button" onClick={() => removeCatalogRule(rule)} disabled={savingRule === `rule:${rule.id}`} style={{ border:'1px solid #FECACA', background:savingRule === `rule:${rule.id}` ? '#F3F4F6' : '#FEF2F2', color:savingRule === `rule:${rule.id}` ? '#6B7280' : '#B91C1C', borderRadius:6, padding:'6px 9px', fontSize:11, fontWeight:900, cursor:savingRule === `rule:${rule.id}` ? 'wait' : 'pointer' }}>
                            {savingRule === `rule:${rule.id}` ? 'Removing...' : 'Remove'}
                          </button>
                        ) : <span style={{ color:'#9CA3AF' }}>-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <div style={{ color:'#6B7280', fontSize:11, fontWeight:700 }}>
        Version {data?.version || 'OCF'}{data?.generatedAt ? `, generated ${new Date(data.generatedAt).toLocaleString([], { dateStyle:'medium', timeStyle:'short' })}` : ''}
      </div>
    </div>
  );
};
